/**
 * Transfer Modal Component
 * Multi-step flow for transferring a reward to another user
 */

import { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemButton,
  Avatar,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  InputAdornment,
  IconButton,
  Divider,
} from '@mui/material'
import {
  Close,
  Search,
  Send,
  CheckCircle,
  ArrowBack,
  Person,
} from '@mui/icons-material'
import { useQuery } from '@tanstack/react-query'
import { socialApi } from '@api/endpoints/social'
import { useTransferReward } from '../hooks/useRewardInstance'

interface TransferModalProps {
  open: boolean
  onClose: () => void
  rewardId: string
  rewardTitle: string
  onSuccess?: () => void
}

type TransferStep = 'confirm' | 'select' | 'review' | 'success'

interface SelectedUser {
  userId: string
  displayName: string
  avatarUrl?: string
}

const steps = ['Confirm', 'Select Recipient', 'Review']

export function TransferModal({
  open,
  onClose,
  rewardId,
  rewardTitle,
  onSuccess,
}: TransferModalProps) {
  const [currentStep, setCurrentStep] = useState<TransferStep>('confirm')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedUser, setSelectedUser] = useState<SelectedUser | null>(null)
  const [error, setError] = useState<string | null>(null)

  const transferMutation = useTransferReward()

  // Fetch friends for selection
  const { data: friends, isLoading: isLoadingFriends } = useQuery({
    queryKey: ['friends'],
    queryFn: () => socialApi.getFriends(),
    enabled: open && currentStep === 'select',
  })

  const handleClose = () => {
    setCurrentStep('confirm')
    setSearchQuery('')
    setSelectedUser(null)
    setError(null)
    onClose()
  }

  const handleSelectUser = (user: SelectedUser) => {
    setSelectedUser(user)
    setCurrentStep('review')
  }

  const handleTransfer = async () => {
    if (!selectedUser) return

    setError(null)
    try {
      const result = await transferMutation.mutateAsync({
        instanceId: rewardId,
        toUserId: selectedUser.userId,
      })

      if (result.success) {
        setCurrentStep('success')
      } else {
        setError(result.message || 'Failed to transfer reward')
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to transfer reward. Please try again.')
    }
  }

  const handleSuccessClose = () => {
    handleClose()
    onSuccess?.()
  }

  const filteredFriends = friends?.filter((friend) =>
    friend.displayName?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const getStepIndex = () => {
    switch (currentStep) {
      case 'confirm':
        return 0
      case 'select':
        return 1
      case 'review':
      case 'success':
        return 2
      default:
        return 0
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">Transfer Reward</Typography>
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      {currentStep !== 'success' && (
        <Box sx={{ px: 3 }}>
          <Stepper activeStep={getStepIndex()} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
      )}

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Step 1: Confirm */}
        {currentStep === 'confirm' && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Send sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Transfer "{rewardTitle}"?
            </Typography>
            <Alert severity="warning" sx={{ mb: 2, textAlign: 'left' }}>
              <Typography variant="body2">
                <strong>This action cannot be undone.</strong> Once transferred, the reward will
                belong to the recipient and you will no longer have access to it.
              </Typography>
            </Alert>
          </Box>
        )}

        {/* Step 2: Select recipient */}
        {currentStep === 'select' && (
          <Box>
            <TextField
              fullWidth
              placeholder="Search friends..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />

            {isLoadingFriends ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : filteredFriends && filteredFriends.length > 0 ? (
              <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                {filteredFriends.map((friend) => (
                  <ListItem key={friend.userId} disablePadding>
                    <ListItemButton
                      onClick={() =>
                        handleSelectUser({
                          userId: friend.userId,
                          displayName: friend.displayName || 'Unknown User',
                          avatarUrl: friend.avatarUrl,
                        })
                      }
                    >
                      <ListItemAvatar>
                        <Avatar src={friend.avatarUrl}>
                          <Person />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={friend.displayName}
                        secondary={friend.username ? `@${friend.username}` : undefined}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">
                  {searchQuery
                    ? 'No friends found matching your search'
                    : 'No friends to transfer to. Add friends to transfer rewards!'}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Step 3: Review */}
        {currentStep === 'review' && selectedUser && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              You are about to transfer
            </Typography>
            <Typography variant="h6" gutterBottom>
              "{rewardTitle}"
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary" gutterBottom>
              To
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
              <Avatar src={selectedUser.avatarUrl} sx={{ width: 56, height: 56 }}>
                <Person />
              </Avatar>
              <Typography variant="h6">{selectedUser.displayName}</Typography>
            </Box>
          </Box>
        )}

        {/* Step 4: Success */}
        {currentStep === 'success' && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Transfer Complete!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              "{rewardTitle}" has been transferred to {selectedUser?.displayName}.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {currentStep === 'confirm' && (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button variant="contained" onClick={() => setCurrentStep('select')}>
              Continue
            </Button>
          </>
        )}

        {currentStep === 'select' && (
          <>
            <Button startIcon={<ArrowBack />} onClick={() => setCurrentStep('confirm')}>
              Back
            </Button>
          </>
        )}

        {currentStep === 'review' && (
          <>
            <Button startIcon={<ArrowBack />} onClick={() => setCurrentStep('select')}>
              Back
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={
                transferMutation.isPending ? <CircularProgress size={20} /> : <Send />
              }
              onClick={handleTransfer}
              disabled={transferMutation.isPending}
            >
              {transferMutation.isPending ? 'Transferring...' : 'Confirm Transfer'}
            </Button>
          </>
        )}

        {currentStep === 'success' && (
          <Button variant="contained" onClick={handleSuccessClose}>
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
